import RichTextToolbar from "@/components/eventEditor/RichTextToolbar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import draftToHtml from "@/components/writeMail/draftToHtml";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import CreateAgendaItemData from "@/domain/entities/agenda/CreateAgendaItemData";
import UpdateAgendaItemData from "@/domain/entities/agenda/UpdateAgendaItemData";
import useCreateAgendaEvent from "@/hooks/agenda/useCreateAgendaEvent";
import useUpdateAgendaEvent from "@/hooks/agenda/useUpdateAgendaEvent";
import useSetEventEditor from "@/hooks/useSetEventEditor";
import useErrorToast from "@/hooks/utils/useErrorToast";
import {
    startOfCalendarDay,
    toAllDayApiEndDate,
    toAllDayDisplayEndDate,
} from "@/lib/agendaAllDayDates";
import { cn } from "@/lib/utils";
import { EditorContent } from "@tiptap/react";
import {
    ArrowLeft,
    CalendarClock,
    Loader2,
    MapPin,
    NotebookPen,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import createDateTimeString from "./createDateTimeString";
import EventAttendeesInput from "./EventAttendeesInput";
import EventDayPicker from "./EventDayPicker";
import { eventFormTone, type EventFormTone } from "./eventFormTone";
import EventTimePicker from "./EventTimePicker";

type EventEditFormProps = {
    event: AgendaItem;
    setIsEditing?: (isEditing: boolean) => void;
    onSuccess?: () => void;
    /**
     * The surface the form sits on: the calendar's purple modal (default, unchanged), or a white card
     * of the assistant's chat (`light`).
     */
    tone?: EventFormTone;
    /** `light` only: « Annuler », next to the save button. */
    onCancel?: () => void;
    /** `light` only: the save button's label (default: « Enregistrer »). */
    submitLabel?: string;
};

// The two buttons of the light form: those of the assistant's cards (assistantClasses.ts).
const LIGHT_SECONDARY_BUTTON =
    "h-8 rounded-lg border border-assistant-border bg-white px-3 text-[13px] font-medium text-primary-darkest hover:bg-primary-lightest";
const LIGHT_PRIMARY_BUTTON =
    "inline-flex h-8 min-w-25 items-center justify-center gap-1.5 rounded-lg border-0 bg-primary-dark px-3 text-[13px] font-medium text-white hover:bg-primary-darker disabled:opacity-60";

const EDITOR_CONTENT_CLASSES = [
    "w-full h-full",
    "cursor-text",
    "p-4",
    "text-start",
    "overflow-wrap-anywhere",
    "text-sm",
    "[&_.ProseMirror]:outline-none",
    "[&_ul]:list-disc [&_ul]:pl-4",
    "[&_ol]:list-decimal [&_ol]:pl-4",
].join(" ");

const EventEditForm = ({
    event,
    setIsEditing,
    onSuccess,
    tone = "dark",
    onCancel,
    submitLabel,
}: EventEditFormProps) => {
    const { t } = useTranslation();
    const light = tone === "light";
    const toneClasses = eventFormTone[tone];
    const showErrorToast = useErrorToast();

    // In the assistant (light look), the agenda is the model's plain text: escaped into paragraphs, as
    // « Créer l'événement » sends it, never read as HTML.
    const { editor } = useSetEventEditor({
        content:
            event?.body ||
            (light && event?.bodyPreview
                ? draftToHtml(event.bodyPreview)
                : event?.bodyPreview) ||
            "",
    });

    const [subject, setSubject] = useState<string>(event?.subject ?? "");
    const [startDate, setStartDate] = useState<Date | undefined>(
        event?.isAllDayEvent() && event.start
            ? startOfCalendarDay(event.start)
            : event?.start,
    );
    const [endDate, setEndDate] = useState<Date | undefined>(
        event && event?.isAllDayEvent() && event.end && event.start
            ? toAllDayDisplayEndDate(event.start, event.end)
            : event?.end,
    );
    const [startTime, setStartTime] = useState<string>(
        `${event?.start.getHours().toString().padStart(2, "0")}:${event?.start.getMinutes().toString().padStart(2, "0")}`,
    );
    const [endTime, setEndTime] = useState<string>(
        `${event?.end.getHours().toString().padStart(2, "0")}:${event?.end.getMinutes().toString().padStart(2, "0")}`,
    );
    const [isAllDay, setIsAllDay] = useState<boolean | undefined>(
        event?.isAllDayEvent(),
    );
    const [location, setLocation] = useState<string | undefined>(
        event?.location?.displayName,
    );
    const [attendees, setAttendees] = useState<string[]>(
        event?.attendees.map((attendee) => attendee.emailAddress.address) ?? [],
    );

    const { updateAgendaEvent } = useUpdateAgendaEvent(onSuccess);
    const { createAgendaEvent } = useCreateAgendaEvent(onSuccess);

    const isLoading =
        updateAgendaEvent.isPending || createAgendaEvent.isPending;

    const handleFormSubmit = () => {
        const startDateTime = createDateTimeString(
            startDate,
            isAllDay ? "00:00" : startTime,
            !!isAllDay,
        );

        const endDateTime = createDateTimeString(
            isAllDay && endDate ? toAllDayApiEndDate(endDate) : endDate,
            isAllDay ? "00:00" : endTime,
            !!isAllDay,
        );

        if (!startDateTime || !endDateTime || !subject) {
            showErrorToast({
                titleTranslationKey: t("errors.agenda.missingProperties"),
                descriptionTranslationKey: t(
                    "errors.agenda.missingPropertiesDescription",
                ),
            });

            return;
        }

        if (startDateTime > endDateTime) {
            showErrorToast({
                titleTranslationKey: t("errors.agenda.endTimeBeforeStartTime"),
                descriptionTranslationKey: t(
                    "errors.agenda.endTimeBeforeStartTimeDescription",
                ),
            });

            return;
        }

        if (!event.isNewEvent()) {
            const updateAgendaItemData = new UpdateAgendaItemData({
                location,
                subject,
                timeUpdate:
                    startDateTime && endDateTime
                        ? {
                              start: startDateTime,
                              end: endDateTime,
                              isAllDay,
                          }
                        : undefined,
                attendees,
                description: editor?.getHTML(),
            });

            updateAgendaEvent.mutate({
                agendaItemId: event.id,
                updateAgendaItemData,
            });
        } else {
            const createAgendaItemData = new CreateAgendaItemData({
                location,
                subject,
                start: startDateTime,
                end: endDateTime,
                attendees,
                description: editor?.getHTML(),
                isAllDay,
            });

            createAgendaEvent.mutate({
                createAgendaItemData,
            });
        }
    };

    return (
        <form
            className={cn(
                "flex h-full flex-col justify-between",
                light ? "gap-4 text-primary-darkest" : "gap-6 py-2",
            )}
            onSubmit={(e) => {
                e.preventDefault();
                handleFormSubmit();
            }}
        >
            <ScrollArea
                className={light ? "flex-1" : "my-1 mr-1 flex-1 px-6 py-2"}
            >
                <div
                    className={cn(
                        "flex max-h-[650px] flex-1 flex-col",
                        light ? "gap-4 p-px" : "gap-6",
                    )}
                >
                    <div
                        className={cn(
                            "flex items-center gap-2",
                            !light && "pr-6",
                        )}
                    >
                        {!event.isNewEvent() && (
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsEditing?.(false);
                                }}
                                variant="invisible"
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        )}
                        <Input
                            className={cn(
                                "border-b-1 rounded-none border-l-0 border-r-0 border-t-0 pl-1 focus-visible:ring-0",
                                toneClasses.placeholder,
                                light
                                    ? "border-assistant-border font-semibold md:text-[15px]"
                                    : "md:text-xl",
                            )}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder={t(
                                "dashboard.widgets.calendar.event.subject",
                            )}
                            value={subject}
                        />
                    </div>
                    <div>
                        <div className="flex gap-3">
                            <CalendarClock
                                className={cn(
                                    "mt-2.5 h-4 w-4 flex-shrink-0",
                                    toneClasses.icon,
                                )}
                            />
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <EventDayPicker
                                            date={startDate}
                                            id="start"
                                            setDate={setStartDate}
                                            tone={tone}
                                        />

                                        {!isAllDay && (
                                            <EventTimePicker
                                                setTime={setStartTime}
                                                time={startTime}
                                                tone={tone}
                                            />
                                        )}
                                    </div>
                                    <div>-</div>
                                    <div className="flex items-center gap-2">
                                        <EventDayPicker
                                            date={endDate}
                                            id="end"
                                            setDate={setEndDate}
                                            tone={tone}
                                        />
                                        {!isAllDay && (
                                            <EventTimePicker
                                                setTime={setEndTime}
                                                time={endTime}
                                                tone={tone}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        checked={isAllDay}
                                        className={toneClasses.checkbox}
                                        id="all-day-checkbox"
                                        onCheckedChange={() =>
                                            setIsAllDay(!isAllDay)
                                        }
                                    />
                                    <Label
                                        className="cursor-pointer"
                                        htmlFor="all-day-checkbox"
                                    >
                                        {t(
                                            "dashboard.widgets.calendar.event.allDay",
                                        )}
                                    </Label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <MapPin
                            className={cn(
                                "mt-[-2px] h-4 w-4 flex-shrink-0",
                                toneClasses.icon,
                            )}
                        />
                        <Input
                            className={cn(
                                "flex items-center gap-2 rounded-md border px-3 py-1",
                                toneClasses.border,
                            )}
                            onChange={(e) => {
                                e.stopPropagation();
                                setLocation(e.target.value);
                            }}
                            value={location}
                        />
                    </div>
                    <EventAttendeesInput
                        attendees={attendees.filter(
                            (attendee) =>
                                attendee !==
                                event?.organizer?.emailAddress.address,
                        )}
                        setAttendees={(values) => {
                            let newAttendees = values;
                            if (event?.organizer?.emailAddress.address) {
                                newAttendees = [
                                    event?.organizer?.emailAddress.address,
                                    ...values,
                                ];
                            }
                            setAttendees(newAttendees);
                        }}
                        tone={tone}
                    />
                    {editor && (
                        <div className="flex items-start gap-2">
                            <NotebookPen
                                className={cn(
                                    "mt-2.5 h-4 w-4 flex-shrink-0",
                                    toneClasses.icon,
                                )}
                            />
                            <div className="flex w-full flex-col">
                                <RichTextToolbar editor={editor} tone={tone} />
                                <ScrollArea
                                    className={cn(
                                        "w-full rounded-bl-md rounded-br-md border border-t-0",
                                        toneClasses.border,
                                    )}
                                    variant="ghost"
                                >
                                    <div className="flex h-full max-h-[300px] w-full">
                                        <EditorContent
                                            className={EDITOR_CONTENT_CLASSES}
                                            editor={editor}
                                        />
                                    </div>
                                </ScrollArea>
                            </div>
                        </div>
                    )}
                </div>
            </ScrollArea>
            {light ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                    {onCancel && (
                        <button
                            className={LIGHT_SECONDARY_BUTTON}
                            disabled={isLoading}
                            onClick={onCancel}
                            type="button"
                        >
                            {t("dashboard.widgets.calendar.event.cancel")}
                        </button>
                    )}
                    <button
                        className={LIGHT_PRIMARY_BUTTON}
                        disabled={
                            !subject || !startDate || !endDate || isLoading
                        }
                        type="submit"
                    >
                        {isLoading && (
                            <Loader2
                                aria-hidden
                                className="size-3.5 animate-spin"
                            />
                        )}
                        {submitLabel ??
                            t("dashboard.widgets.calendar.event.save")}
                    </button>
                </div>
            ) : (
                <div className="m-2 mx-4 flex items-center justify-end gap-2">
                    {!event.isNewEvent() && (
                        <Button
                            className="border-primary-dark bg-black/30 text-white hover:bg-black/50"
                            disabled={isLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsEditing?.(false);
                            }}
                        >
                            {t("dashboard.widgets.calendar.event.cancel")}
                        </Button>
                    )}
                    <Button
                        className="min-w-25 border-primary-dark text-primary-dark"
                        disabled={
                            !subject || !startDate || !endDate || isLoading
                        }
                        type="submit"
                        variant="outline"
                    >
                        {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            t("dashboard.widgets.calendar.event.save")
                        )}
                    </Button>
                </div>
            )}
        </form>
    );
};

export default EventEditForm;
